/**
 * /shopping-list data layer (Plan 17).
 *
 * REST + TanStack Query hooks for the chef-side ingredient
 * aggregation surface. Server primitive is
 * `ShoppingListAppManager`; the GraphQL `shoppingListForKitchen`
 * resolver in `@ordo/graphql` shares the same `@ordo/schema`
 * aggregator underneath.
 *
 * Pages consume `useShoppingListQuery` directly; helpers below
 * (range presets, CSV escaping, category grouping, sort) are pure
 * for unit-test coverage.
 */

import { formatMeasureText } from '@ordo/constants/measure-format'
import { useQuery } from '@tanstack/react-query'

import { apiFetch } from './http'

// ---------------------------------------------------------------------------
// Types — match the server payload byte-for-byte (REST is the
// canonical client transport per apps/ordo §API style).
// ---------------------------------------------------------------------------

export type ShoppingListCategory = 'FOOD' | 'SUPPLY' | 'UNCATEGORIZED'

/**
 * Which demand fed a row: `orders` = placed shipments, `forecast` =
 * menu-derived look-ahead estimate, `mixed` = both (range spans actual
 * and forecast-filled days).
 */
export type ShoppingListRowSource = 'orders' | 'forecast' | 'mixed'

export interface ShoppingListIngredient {
  id: string
  name: string
  category: ShoppingListCategory
  /** USDA CNDB category name ("Spices and Herbs", "Poultry", ...)
   *  via the catalog's `usdaCndbFoodId` link; null = no USDA mapping
   *  (SUPPLY items, unmapped foods) — lands in the "Other" bucket. */
  usdaCategory: string | null
}

/** Which decision produced a row's brand — same ladder as kitchen-orders. */
export type ShoppingListBrandSource = 'PINNED' | 'KITCHEN' | 'VARIETY' | 'DEFAULT' | null

export interface ShoppingListAggregationRow {
  ingredient: ShoppingListIngredient
  /** Resolved brand label (pin → kitchen selection → variety → default). */
  brandLabel: string | null
  /** The option `brandLabel` resolved to; null for variety/generic lines. */
  brandOptionId: string | null
  brandSource: ShoppingListBrandSource
  /** False when a recipe pin (or menu chef selection) fixes the brand. */
  brandSelectable: boolean
  /** Live brand options — the dropdown only appears with two or more. */
  brandOptionCount: number
  /** ACTUAL (placed-order) demand only — forecast never mixes in. */
  totalRawQuantity: number
  totalRawUnit: string
  totalPurchaseUnits: number | null
  purchaseUnit: string | null
  estimatedCostCents: number | null
  sourceShipmentIds: string[]
  usedByComponentIds: string[]
  source: ShoppingListRowSource
  /**
   * Menu-derived estimate for zero-shipment days (same unit as
   * `totalRawUnit`); null = the contributing component(s) have no
   * ordering history yet — render "no history", never a made-up 0.
   */
  forecastRawQuantity: number | null
  forecastMissingHistory: boolean
}

export interface ShoppingListAggregation {
  rows: ShoppingListAggregationRow[]
  incompleteComponentCount: number
  shipmentCount: number
  schoolCount: number
  /** Zero-shipment dates (`YYYY-MM-DD`) filled with forecast demand. */
  forecastDays: string[]
  forecastDayCount: number
  /** Per-(day, school) recipes the forecast expects the kitchen to make —
   *  the Recipes tab's rows for days with no placed orders yet. Recipe-backed,
   *  non-condiment components only (what the kitchen COOKS). */
  forecastRecipes: ShoppingListForecastRecipe[]
  /** Additive for compatibility with servers deployed before this field. */
  forecastMenuItems?: ShoppingListForecastRecipe[]
}

/** One recipe-backed component forecast for a zero-shipment day + school. */
export interface ShoppingListForecastRecipe {
  componentVariantId?: string | null
  /** `YYYY-MM-DD` service date. */
  date: string
  componentId: string
  componentName: string
  /** Catalog `ComponentKind` (MAIN_ENTREE / SIDE / ...) — type grouping. */
  componentKind: string
  schoolName: string
  /** History-averaged servings; null = no ordering history (unknown, not 0). */
  estimatedServings: number | null
}

/**
 * Rows a printed/exported document may include: placed-order demand
 * only. Forecast-only rows are dropped; `mixed` rows survive because
 * `totalRawQuantity` is actuals-only by construction, so the printed
 * quantity reflects placed orders exclusively. Pure for unit tests.
 */
/**
 * Project one aggregation row onto the brand-picker's structural input —
 * the shape `IngredientBrandPicker` (chefs) consumes. Pure for unit tests.
 */
export function shoppingListRowBrand(row: ShoppingListAggregationRow): {
  name: string
  brand: string | null
  ingredientId: string
  brandOptionId: string | null
  brandSource: ShoppingListBrandSource
  brandSelectable: boolean
  brandOptionCount: number
} {
  return {
    name: row.ingredient.name,
    brand: row.brandLabel,
    ingredientId: row.ingredient.id,
    brandOptionId: row.brandOptionId,
    brandSource: row.brandSource,
    brandSelectable: row.brandSelectable,
    brandOptionCount: row.brandOptionCount,
  }
}

export function printableShoppingListRows(
  rows: ReadonlyArray<ShoppingListAggregationRow>,
): ShoppingListAggregationRow[] {
  return rows.filter((r) => r.source !== 'forecast')
}

// ---------------------------------------------------------------------------
// Date helpers — UTC-anchored to match the server's interpretation
// of `?start=YYYY-MM-DD&end=YYYY-MM-DD`. Re-exported so the page
// + the range picker share the math.
// ---------------------------------------------------------------------------

export function ymdUtc(d: Date): string {
  const y = d.getUTCFullYear().toString().padStart(4, '0')
  const m = (d.getUTCMonth() + 1).toString().padStart(2, '0')
  const dd = d.getUTCDate().toString().padStart(2, '0')
  return `${y}-${m}-${dd}`
}

export function addDaysUtc(d: Date, n: number): Date {
  const out = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()))
  out.setUTCDate(out.getUTCDate() + n)
  return out
}

/**
 * Monday-anchored start-of-week (UTC). Weekends snap FORWARD to the upcoming
 * Monday — same service-week rule as `startOfWorkWeek` in chefs/service-days.
 */
export function startOfWeekMondayUtc(d: Date): Date {
  const out = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()))
  const dow = out.getUTCDay()
  const delta = dow === 0 ? 1 : dow === 6 ? 2 : 1 - dow
  out.setUTCDate(out.getUTCDate() + delta)
  return out
}

/** Sunday-anchored end-of-week (UTC). Inclusive — the chef sees
 * Mon-Sun in the picker. */
export function endOfWeekSundayUtc(d: Date): Date {
  return addDaysUtc(startOfWeekMondayUtc(d), 6)
}

export function startOfMonthUtc(d: Date): Date {
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1))
}

export function endOfMonthUtc(d: Date): Date {
  return new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0))
}

// ---------------------------------------------------------------------------
// Range presets — what the chips on the range picker resolve to.
// "This week + next week" is the Plan 17 default per the spec.
// ---------------------------------------------------------------------------

export type ShoppingListRangePresetId = 'this-week' | 'this-week-plus-next' | 'this-month' | 'custom'

export interface ShoppingListRange {
  start: Date
  end: Date
}

export function computeRangePreset(preset: ShoppingListRangePresetId, now: Date): ShoppingListRange {
  const today = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()))
  if (preset === 'this-week') {
    return { start: startOfWeekMondayUtc(today), end: endOfWeekSundayUtc(today) }
  }
  if (preset === 'this-week-plus-next') {
    return { start: startOfWeekMondayUtc(today), end: addDaysUtc(endOfWeekSundayUtc(today), 7) }
  }
  if (preset === 'this-month') {
    return { start: startOfMonthUtc(today), end: endOfMonthUtc(today) }
  }
  // Fallback for custom — return the default so callers don't crash.
  return { start: startOfWeekMondayUtc(today), end: addDaysUtc(endOfWeekSundayUtc(today), 7) }
}

/**
 * Human label for a range — used by the page header and the range
 * picker trigger button. Pure for unit tests.
 *
 *   "May 25 – Jun 7, 2026"
 *   "May 25, 2026"  (single-day range)
 */
export function formatRangeLabel(range: ShoppingListRange): string {
  const fmt = (d: Date) =>
    d.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      timeZone: 'UTC',
    })
  if (ymdUtc(range.start) === ymdUtc(range.end)) {
    return fmt(range.start)
  }
  // Same year? Drop the year on the first half for compactness.
  if (range.start.getUTCFullYear() === range.end.getUTCFullYear()) {
    const a = range.start.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })
    return `${a} – ${fmt(range.end)}`
  }
  return `${fmt(range.start)} – ${fmt(range.end)}`
}

// ---------------------------------------------------------------------------
// Aggregation helpers — sort + group + render + CSV. Pure for tests.
// ---------------------------------------------------------------------------

export type ShoppingListSortField = 'category' | 'name' | 'cost'
export type ShoppingListSortDirection = 'asc' | 'desc'

export interface ShoppingListSort {
  field: ShoppingListSortField
  direction: ShoppingListSortDirection
}

/**
 * Sort rows according to the requested column. Cost falls back to
 * -Infinity for nullable costs so they sink to the bottom in ascending
 * sort (chef sees uncosted rows last when sorting by money).
 */
export function sortShoppingListRows(
  rows: ReadonlyArray<ShoppingListAggregationRow>,
  sort: ShoppingListSort,
): ShoppingListAggregationRow[] {
  const dir = sort.direction === 'desc' ? -1 : 1
  const out = [...rows]
  out.sort((a, b) => {
    if (sort.field === 'category') {
      const byCat = a.ingredient.category.localeCompare(b.ingredient.category)
      if (byCat !== 0) {
        return byCat * dir
      }
      // Inside a category, secondary key on name (ascending always —
      // the chef expects alphabetical within a group).
      return a.ingredient.name.localeCompare(b.ingredient.name)
    }
    if (sort.field === 'name') {
      return a.ingredient.name.localeCompare(b.ingredient.name) * dir
    }
    // cost — nulls sink to the bottom regardless of direction.
    const aCost = a.estimatedCostCents
    const bCost = b.estimatedCostCents
    if (aCost === null && bCost === null) {
      return a.ingredient.name.localeCompare(b.ingredient.name)
    }
    if (aCost === null) {
      return 1
    }
    if (bCost === null) {
      return -1
    }
    return (aCost - bCost) * dir
  })
  return out
}

/** Group rows by category, preserving the relative order of the input. */
export function groupRowsByCategory(
  rows: ReadonlyArray<ShoppingListAggregationRow>,
): Array<{ category: ShoppingListCategory; rows: ShoppingListAggregationRow[] }> {
  const order: ShoppingListCategory[] = []
  const map = new Map<ShoppingListCategory, ShoppingListAggregationRow[]>()
  for (const r of rows) {
    const cat = r.ingredient.category
    if (!map.has(cat)) {
      map.set(cat, [])
      order.push(cat)
    }
    map.get(cat)!.push(r)
  }
  return order.map((cat) => ({ category: cat, rows: map.get(cat)! }))
}

/**
 * Render the quantity column the chef sees in the table.
 *
 * PURCHASING rendering: one unit, no fractions, no conversion — you buy by
 * weight, and this column feeds the CSV export.
 */
export function formatQuantity(value: number, unit: string): string {
  return formatMeasureText(value, unit, { system: 'as-authored', use: 'PURCHASING' })
}

/**
 * Render cost cents → `$1,234.56` or `—` when null. Used by the
 * table + the drawer cost line.
 */
export function formatCostCents(cents: number | null): string {
  if (cents === null) {
    return '—'
  }
  const dollars = cents / 100
  return `$${dollars.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

/** Sum costed rows for the footer total. */
export function sumCostedCents(rows: ReadonlyArray<ShoppingListAggregationRow>): {
  totalCents: number
  uncostedCount: number
} {
  let totalCents = 0
  let uncostedCount = 0
  for (const r of rows) {
    if (r.estimatedCostCents === null) {
      uncostedCount += 1
    } else {
      totalCents += r.estimatedCostCents
    }
  }
  return { totalCents, uncostedCount }
}

const CATEGORY_LABELS: Record<ShoppingListCategory, string> = {
  FOOD: 'Food',
  SUPPLY: 'Supplies',
  UNCATEGORIZED: 'Uncategorized',
}

export function categoryLabel(category: ShoppingListCategory): string {
  return CATEGORY_LABELS[category]
}

// ---------------------------------------------------------------------------
// CSV export — escapes per RFC 4180 (double-quote anything with a
// comma / quote / newline; double internal quotes).
// ---------------------------------------------------------------------------

export function escapeCsv(value: string): string {
  if (value === '') {
    return value
  }
  if (/[",\n\r]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`
  }
  return value
}

export function buildShoppingListCsv(rows: ReadonlyArray<ShoppingListAggregationRow>): string {
  const headers = ['Category', 'Ingredient', 'Quantity', 'Unit', 'Purchase Unit', 'Estimated Cost (USD)']
  const lines: string[] = [headers.join(',')]
  for (const r of rows) {
    lines.push(
      [
        escapeCsv(categoryLabel(r.ingredient.category)),
        escapeCsv(r.ingredient.name),
        escapeCsv(String(r.totalRawQuantity)),
        escapeCsv(r.totalRawUnit),
        escapeCsv(r.purchaseUnit ?? ''),
        escapeCsv(r.estimatedCostCents === null ? '' : (r.estimatedCostCents / 100).toFixed(2)),
      ].join(','),
    )
  }
  return lines.join('\n')
}

/** Trigger a browser download of the CSV. Defensive — caller can
 * guard SSR by checking for `document` if needed. */
export function downloadShoppingListCsv(rows: ReadonlyArray<ShoppingListAggregationRow>, filename?: string): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return
  }
  const csv = buildShoppingListCsv(rows)
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename ?? `shopping-list-${ymdUtc(new Date())}.csv`
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

// ---------------------------------------------------------------------------
// Fetch + query
// ---------------------------------------------------------------------------

export interface ShoppingListQueryVariables {
  kitchenLocationId: string
  /** YYYY-MM-DD inclusive. */
  start: string
  /** YYYY-MM-DD inclusive. */
  end: string
  /**
   * Fill zero-shipment days (today or later) with a menu-derived
   * forecast. The page sends this only while the range's order window
   * is still open, so a closed range can never show forecast rows.
   */
  includeForecast?: boolean
}

export async function fetchShoppingList(
  vars: ShoppingListQueryVariables,
  signal?: AbortSignal,
): Promise<ShoppingListAggregation> {
  return apiFetch<ShoppingListAggregation>('/api/shopping-list', {
    query: {
      kitchenLocationId: vars.kitchenLocationId,
      start: vars.start,
      end: vars.end,
      // Strictly opt-in: omit entirely unless requested so the wire
      // shape of existing (no-forecast) requests is unchanged.
      ...(vars.includeForecast ? { includeForecast: 'true' } : {}),
    },
    signal,
  })
}

export const shoppingListQueryKey = (vars: ShoppingListQueryVariables) => ['shopping-list', vars] as const

export function useShoppingListQuery(vars: ShoppingListQueryVariables, opts: { enabled?: boolean } = {}) {
  return useQuery<ShoppingListAggregation>({
    queryKey: shoppingListQueryKey(vars),
    queryFn: ({ signal }) => fetchShoppingList(vars, signal),
    enabled: (opts.enabled ?? true) && Boolean(vars.kitchenLocationId),
    // Shipments don't change often once scheduled — a 5-minute stale
    // time matches the cache hint in the plan markdown.
    staleTime: 5 * 60_000,
  })
}
